/**
 * @file 策略覆盖模块
 * @description 按平台条件将自定义实现合并到已注册的策略实例上，
 *              支持在 bootstrap 阶段覆盖 navigation / communication / location 能力域方法
 */

import type { PlatformType, StrategyOverride, IPlatformStrategy } from './types'

/**
 * 判断覆盖项是否对指定平台生效
 * @param current - 当前检测到的平台类型
 * @param override - 单个覆盖项
 * @returns 是否匹配
 */
export function matchPlatform(current: PlatformType, override: StrategyOverride): boolean {
  // 不限制平台：所有平台都生效
  if (override.platform == null) return true
  // 数组：任一平台匹配即生效
  if (Array.isArray(override.platform)) {
    return override.platform.includes(current)
  }
  // 单值：精确匹配
  return override.platform === current
}

/**
 * 将匹配当前平台的 override 项合并到策略实例上
 * 直接修改 strategy 对象，被覆盖方法永久替换为自定义实现
 * @param strategy - 当前已注册的平台策略实例
 * @param overrides - 覆盖项数组，可为空
 * @param currentType - 当前平台类型（用于条件筛选）
 */
export function applyOverrides(
  strategy: IPlatformStrategy,
  overrides: StrategyOverride[] | undefined,
  currentType: PlatformType
): void {
  // 不传或空数组：直接返回，保持原策略不变（向后兼容）
  if (!overrides?.length) return

  overrides.forEach((override) => {
    // 平台不匹配则跳过该覆盖项
    if (!matchPlatform(currentType, override)) return

    // 逐域浅合并：用户只写要覆盖的方法，其余保留原策略实现
    if (override.navigation) {
      Object.assign(strategy.navigation, override.navigation)
    }
    if (override.communication) {
      Object.assign(strategy.communication, override.communication)
    }
    if (override.location) {
      Object.assign(strategy.location, override.location)
    }
  })
}
